import {ColorDecorator} from '@github-ui/shared-single-select-components/ColorDecorator'
import {testIdProps} from '@github-ui/test-id-props'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import type {ReactNode} from 'react'
import {useCallback, useMemo} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {PersistedOption} from '../../api/columns/contracts/single-select'
import {AutoAssignItemStatusChangeName, type AutoAssignItemStatusChangeUIType} from '../../api/stats/contracts'
import {SanitizedHtml} from '../../components/dom/sanitized-html'
import {parseTextFromHtmlStr} from '../../helpers/parsing'
import type {SuggestedSingleSelectOption} from '../../helpers/suggestions'
import {isAutoAssignAgentWorkflow} from '../../helpers/workflow-utilities'
import type {ColumnModelForDataType} from '../../models/column-model'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {usePostStats} from '../common/use-post-stats'
import {useUpdateItem} from '../use-update-item'
import styles from './use-single-select-editor.module.css'

export const getName = (option: PersistedOption) => {
  return option.name
}

export const convertOptionToItem = (
  option: SuggestedSingleSelectOption,
  showColorDecorators: boolean,
  titleTrailingContent?: ReactNode,
): ItemProps & SuggestedSingleSelectOption => ({
  ...option,
  ...testIdProps('table-cell-editor-row'),
  children: (
    <>
      <span className={styles.OptionTitle}>
        <SanitizedHtml className={styles.SanitizedHtml}>{option.nameHtml}</SanitizedHtml>
        {titleTrailingContent}
      </span>
      {/* Rendering the description here allows us to properly support html entities */}
      <SanitizedHtml className={styles.SanitizedHtml_1}>{parseTextFromHtmlStr(option.descriptionHtml)}</SanitizedHtml>
    </>
  ),
  description: '',
  leadingVisual: showColorDecorators
    ? () => <ColorDecorator color={option.color} className={styles.ColorDecorator} />
    : undefined,
  descriptionVariant: 'block',
})

type UseSingleSelectEditorProps = {
  model: MemexItemModel
  columnModel: ColumnModelForDataType<typeof MemexColumnDataType.SingleSelect>
  selectedValueId: string | null
  onSaved?: () => void
  /**
   * UI surface this editor is rendered in. Used to attribute the
   * `auto_assign_item_status_change` stat when a user moves an item to a
   * status configured as the source column of an auto-assign workflow.
   */
  autoAssignStatusChangeUi?: AutoAssignItemStatusChangeUIType
}

export function useSingleSelectEditor({
  model,
  columnModel,
  selectedValueId,
  onSaved,
  autoAssignStatusChangeUi,
}: UseSingleSelectEditorProps) {
  const {updateItem} = useUpdateItem()
  const {workflowsUsingColumnOption} = useWorkflows()
  const {postStats} = usePostStats()

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedSingleSelectOption>) => {
      const nextOptionId = nextSelected[0]?.id ?? null
      await updateItem(model, {
        memexProjectColumnId: columnModel.id as number,
        dataType: MemexColumnDataType.SingleSelect,
        value: {id: nextOptionId},
        isIssueField: columnModel.isIssueField,
      })

      // If the new option is the source column of an auto-assign workflow, emit a stat.
      if (autoAssignStatusChangeUi && nextOptionId && nextOptionId !== selectedValueId) {
        const matchingWorkflows = workflowsUsingColumnOption(columnModel.id as number, nextOptionId)
        const triggersAutoAssign = matchingWorkflows.some(w => w.enabled && isAutoAssignAgentWorkflow(w))
        if (triggersAutoAssign) {
          postStats({
            name: AutoAssignItemStatusChangeName,
            ui: autoAssignStatusChangeUi,
            memexProjectColumnId: columnModel.id,
            memexProjectItemId: model.id,
          })
        }
      }

      onSaved?.()
    },
    [
      autoAssignStatusChangeUi,
      columnModel.id,
      columnModel.isIssueField,
      model,
      onSaved,
      postStats,
      selectedValueId,
      updateItem,
      workflowsUsingColumnOption,
    ],
  )

  const options: Array<SuggestedSingleSelectOption> = useMemo(() => {
    return columnModel.settings.options.map((option: PersistedOption) => {
      return {
        ...option,
        selected: option.id === selectedValueId,
      }
    })
  }, [columnModel.settings, selectedValueId])

  const selected = useMemo(() => options.filter(o => o.selected), [options])
  return {saveSelected, options, selected}
}
